/* LDB Duel — Bundle Design Studio (assets/bundle-studio.js)
 *
 * The tabbed redesign of the bundle designer (Mert, 2026-10-01 sign-off).
 * One artwork upload drives every custom item in the bundle. The studio is
 * a tab rail — "Upload Your Art Here" plus one tab per product — and each
 * product tab holds that item's 2D design view (drag / zoom / rotate, per
 * item) next to its real 3D preview:
 *
 *   playmat      2.5D CSS preview, Flat / On-the-table, zone overlay
 *   deck box     three.js closed box (outer look only, no drawers) with the
 *                wrap art on its three covered faces and the silver die-cut
 *                edges painted from the real traced notch profile
 *   sleeves      three.js sleeve (back film + print + clear front film)
 *   field center three.js gold slab, markings overlay in the chosen colour,
 *                embossed gold back
 *
 * Locked UX rules:
 *   - artwork can be uploaded, replaced or removed ONLY in the Upload tab;
 *     removal asks for confirmation ("removed from all products")
 *   - product tabs edit placement and their own option(s) only: playmat
 *     playzones, deck box Black/White, sleeves Card Size, field center
 *     markings colour (white, black, up to 5 custom swatches + picker)
 *   - option controls are MIRRORS of the bundle cards' own selects — the
 *     cards stay the source of truth, bundle-savings.js keeps gating CTAs
 *   - the four product cards' thumbnails become live previews of each item
 *     from the moment art is uploaded, updating as the design changes
 *
 * At Add to cart the bundle form's single multi-id post is replaced by one
 * /cart/add POST per picked item (unchanged conventions):
 *   properties[Artwork]         the customer's original file
 *   properties[Design Preview]  print-scale placement snapshot for that item
 *   properties[_design]         placement note (+ options that affect print)
 *   properties[_bundle]         shared id so the order groups the lines
 *
 * Fail-safe: the section emits a CSS gate keeping Add to cart inert until
 * this script adds .bst--ready (artwork present).
 */
(function () {
  'use strict';

  var MM_PER_INCH = 25.4;
  var LOW_PPI = { playmat: 100, deckbox: 150, sleeves: 300, fieldcenter: 300 };

  // Print surfaces in mm. The deck box surface is the REAL die-cut wrap
  // sheet (three faces: left side, back, right side), per the Mach 3 spec
  // trace; sleeves switch to 62x89 when the Card Size pick is Japanese.
  var ITEMS = {
    playmat: { label: 'Playmat', w: 609.6, h: 355.6, corner: 10, capW: 1440, safe: 12.7 },
    deckbox: { label: 'Deck Box', w: 284.5, h: 152.4, corner: 2.7, capW: 1422 },
    sleeves: { label: 'Card Sleeves', w: 66, h: 91, corner: 1.6, capW: 1320, border: 1.59 },
    fieldcenter: { label: 'Field Center', w: 45.9, h: 66.3, corner: 2, capW: 1300 }
  };
  var SLEEVE_JP = { w: 62, h: 89 };
  var TYPE_ORDER = ['playmat', 'deckbox', 'sleeves', 'fieldcenter'];

  // ---- Mach 3 deck box die-cut, traced from LDB's spec sheet ------------
  // (same data as the standalone deck box tool's deckbox-diecut.js)
  var NOTCH_PROFILE = [[0.0, 0.0478], [0.025, 0.0708], [0.05, 0.101], [0.075, 0.1431], [0.1, 0.2085], [0.125, 0.3349], [0.15, 0.4743], [0.175, 0.6279], [0.2, 0.7404], [0.225, 0.7892], [0.25, 0.8133], [0.275, 0.8368], [0.3, 0.8573], [0.325, 0.877], [0.35, 0.8964], [0.375, 0.9173], [0.4, 0.9383], [0.425, 0.9581], [0.45, 0.9781], [0.475, 0.9908], [0.5, 0.9976], [0.525, 0.9913], [0.55, 0.9759], [0.575, 0.9583], [0.6, 0.9411], [0.625, 0.922], [0.65, 0.9015], [0.675, 0.8812], [0.7, 0.8626], [0.725, 0.8427], [0.75, 0.8189], [0.775, 0.7878], [0.8, 0.7312], [0.825, 0.6279], [0.85, 0.4876], [0.875, 0.3445], [0.9, 0.2272], [0.925, 0.1292], [0.95, 0.0777], [0.975, 0.0478], [1.0, 0.0261]];
  var LID_SPEC = {
    w: 284.5, h: 152.4,
    panels: [87.5, 109.5, 87.5],   // left side, back, right side
    corner: 2.7, stitchInset: 2.0,
    notches: [
      { top: 8.54, bottom: 39.11, depth: 20.78 },
      { top: 81.82, bottom: 111.72, depth: 20.48 }
    ]
  };
  // box body, for the 3D preview
  var BOX = { w: 109.5, d: 87.5, h: 157.5 };

  /** Die-cut outline onto a 2D context, scaled by pxPerMM. */
  function lidPath(ctx, k) {
    var s = LID_SPEC, W = s.w, H = s.h, r = s.corner;
    function pts(n, side, down) {
      var out = [];
      for (var i = 0; i < NOTCH_PROFILE.length; i++) {
        var p = NOTCH_PROFILE[down ? i : NOTCH_PROFILE.length - 1 - i];
        var y = n.top + (n.bottom - n.top) * p[0];
        var x = side > 0 ? W - n.depth * p[1] : n.depth * p[1];
        out.push([x, y]);
      }
      return out;
    }
    ctx.beginPath();
    ctx.moveTo(r * k, 0);
    ctx.lineTo((W - r) * k, 0);
    ctx.quadraticCurveTo(W * k, 0, W * k, r * k);
    var down = s.notches.slice().sort(function (a, b) { return a.top - b.top; });
    for (var i = 0; i < down.length; i++) {
      var ps = pts(down[i], 1, true);
      for (var j = 0; j < ps.length; j++) ctx.lineTo(ps[j][0] * k, ps[j][1] * k);
    }
    ctx.lineTo(W * k, (H - r) * k);
    ctx.quadraticCurveTo(W * k, H * k, (W - r) * k, H * k);
    ctx.lineTo(r * k, H * k);
    ctx.quadraticCurveTo(0, H * k, 0, (H - r) * k);
    var up = s.notches.slice().sort(function (a, b) { return b.top - a.top; });
    for (var m = 0; m < up.length; m++) {
      var ps2 = pts(up[m], -1, false);
      for (var q = 0; q < ps2.length; q++) ctx.lineTo(ps2[q][0] * k, ps2[q][1] * k);
    }
    ctx.lineTo(0, r * k);
    ctx.quadraticCurveTo(0, 0, r * k, 0);
    ctx.closePath();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-bundle-studio]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__bstInit) continue;
      roots[i].__bstInit = true;
      try {
        new BundleStudio(roots[i]);
      } catch (err) {
        roots[i].__bstInit = false;
        console.error('[bundle-studio] init failed', err);
      }
    }
  }

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function bytesToLabel(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function coverFactor(iw, ih, cw, ch, rot) {
    var a = Math.abs(Math.cos(rot));
    var b = Math.abs(Math.sin(rot));
    return Math.max(cw / (iw * a + ih * b), ch / (iw * b + ih * a));
  }

  function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    if (!(r > 0.5)) { ctx.beginPath(); ctx.rect(x, y, w, h); ctx.closePath(); return; }
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  // ------------------------------------------------------------ constructor

  function BundleStudio(root) {
    this.root = root;
    root.__bst = this;

    this.wrapper = root.closest('bundle-products-card') ||
                   document.querySelector('bundle-products-card');
    this.maxBytes = (parseFloat(root.getAttribute('data-max-mb')) || 15) * 1024 * 1024;
    this.requireArtwork = root.getAttribute('data-require-artwork') === 'true';
    this.threeUrl = root.getAttribute('data-three-url') || '';
    this.fcBaseUrl = root.getAttribute('data-fc-base-url') || '';
    this.fcOverlayWhiteUrl = root.getAttribute('data-fc-overlay-white-url') || '';
    this.fcOverlayBlackUrl = root.getAttribute('data-fc-overlay-black-url') || '';
    this.fcBackUrl = root.getAttribute('data-fc-back-url') || '';
    this.optNames = {
      zone: root.getAttribute('data-playzone-option') || 'Add Playzones',
      matfinish: root.getAttribute('data-matfinish-option') || 'Add Stitched Edging & Playmat thickness',
      cardsize: root.getAttribute('data-cardsize-option') || 'Card Size'
    };
    try {
      this.zoneMap = JSON.parse(root.getAttribute('data-zone-map') || '{}') || {};
    } catch (e) { this.zoneMap = {}; }

    this.rules = this.loadRules();

    this.state = {};
    for (var i = 0; i < TYPE_ORDER.length; i++) {
      this.state[TYPE_ORDER[i]] = { tx: 0, ty: 0, scale: 1, rot: 0 };
    }
    this.art = { img: null, file: null, srcScale: 1 };
    // design-layer choices; card-driven ones are read live from the cards
    this.opts = { markColor: 'white', customColors: [], zoneColor: '#ffffff' };
    this.selected = 'upload';
    this.images = {};
    this.tintCache = {};
    this.stages = {};        // type -> 3D stage record
    this.matView = 'table';  // playmat preview pose
    this.pointers = {};

    this.cacheDom();
    this.bindTabs();
    this.bindUpload();
    this.bindPanes();
    this.buildOptionMirrors();
    this.bindCardLive();
    this.watchBundle();
    this.renderAll();
    this.updateGate();
  }

  BundleStudio.prototype.loadRules = function () {
    var out = { types: {}, products: {} };
    var node = this.wrapper && this.wrapper.querySelector('[data-bundle-rules]');
    var src = null;
    try { src = node ? JSON.parse(node.textContent) : null; } catch (e) { /* defaults */ }
    src = src && typeof src === 'object' ? src : {};
    var types = src.types && typeof src.types === 'object' ? src.types : {
      'deck boxes': 'deckbox', sleeves: 'sleeves', playmat: 'playmat', 'field center': 'fieldcenter'
    };
    for (var k in types) {
      if (typeof types[k] === 'string') out.types[k.trim().toLowerCase()] = types[k].trim().toLowerCase();
    }
    var products = src.products && typeof src.products === 'object' ? src.products : {};
    for (var id in products) {
      if (typeof products[id] === 'string') out.products[String(id)] = products[id].trim().toLowerCase();
    }
    return out;
  };

  BundleStudio.prototype.typeOfProduct = function (productId) {
    var id = String(productId);
    if (this.rules.products[id]) return this.rules.products[id];
    var scope = this.wrapper || document;
    var card = scope.querySelector('bundle-card[data-product-id="' + id + '"]');
    if (!card) return null;
    var t = String(card.dataset.productType || '').trim().toLowerCase();
    return this.rules.types[t] || null;
  };

  /** The bundle card for an item type, through the rules maps. */
  BundleStudio.prototype.cardOfType = function (type) {
    var scope = this.wrapper || document;
    var cards = scope.querySelectorAll('bundle-card[data-product-id]');
    for (var i = 0; i < cards.length; i++) {
      if (this.typeOfProduct(cards[i].dataset.productId) === type) return cards[i];
    }
    return null;
  };

  /** A card's option select whose label text matches (case-insensitive). */
  BundleStudio.prototype.cardOptionSelect = function (card, optionName) {
    if (!card) return null;
    var want = String(optionName).trim().toLowerCase();
    var sels = card.querySelectorAll('[data-bundle-option-select]');
    for (var i = 0; i < sels.length; i++) {
      var label = card.querySelector('label[for="' + sels[i].id + '"]');
      var name = label ? label.textContent.trim().toLowerCase() : '';
      if (name === want || (want && name.indexOf(want) !== -1) ||
          (name && want.indexOf(name) !== -1)) return sels[i];
    }
    return null;
  };

  BundleStudio.prototype.cacheDom = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    this.el = {
      tabs: r.querySelectorAll('[data-bst-tab]'),
      panes: r.querySelectorAll('[data-bst-pane]'),
      fileInput: q('[data-bst-file]'),
      dropzone: q('[data-bst-dropzone]'),
      dropEmpty: q('[data-bst-drop-empty]'),
      dropFull: q('[data-bst-drop-full]'),
      artThumb: q('[data-bst-art-thumb]'),
      upMeta: q('[data-bst-upmeta]'),
      filename: q('[data-bst-filename]'),
      remove: q('[data-bst-remove]'),
      status: q('[data-bst-status]'),
      modal: q('[data-bst-modal]'),
      modalKeep: q('[data-bst-modal-keep]'),
      modalRemove: q('[data-bst-modal-remove]')
    };
  };

  // ------------------------------------------------------------ images

  BundleStudio.prototype.image = function (key, url) {
    if (!url) return null;
    var cached = this.images[key];
    if (cached) return cached.complete && cached.naturalWidth ? cached : null;
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () { self.renderAll(); };
    img.src = url;
    this.images[key] = img;
    return null;
  };

  /** An image refilled with a colour through its own alpha (cached). */
  BundleStudio.prototype.tinted = function (key, img, color) {
    if (!img) return null;
    var hit = this.tintCache[key];
    if (hit && hit.color === color) return hit.canvas;
    var c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    var ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, c.width, c.height);
    this.tintCache[key] = { color: color, canvas: c };
    return c;
  };

  // --------------------------------------------- card-driven option reads

  /** Deck box colour from its card's flat variant select ('black'|'white'). */
  BundleStudio.prototype.boxColor = function () {
    var card = this.cardOfType('deckbox');
    var flat = card && card.querySelector('[data-bundle-variant-select]');
    var opt = flat && flat.options[flat.selectedIndex];
    var name = opt ? String(opt.dataset.variantName || opt.textContent || '') : '';
    return /white/i.test(name) ? 'white' : 'black';
  };

  /** Sleeve geometry from the card's Card Size pick. */
  BundleStudio.prototype.sleeveSize = function () {
    var card = this.cardOfType('sleeves');
    var sel = this.cardOptionSelect(card, this.optNames.cardsize);
    return sel && /japan/i.test(sel.value || '') ? SLEEVE_JP : { w: ITEMS.sleeves.w, h: ITEMS.sleeves.h };
  };

  /** Playzone overlay value from the card's playzone select ('' = none). */
  BundleStudio.prototype.zoneValue = function () {
    var card = this.cardOfType('playmat');
    var sel = this.cardOptionSelect(card, this.optNames.zone);
    var v = sel ? String(sel.value || '') : '';
    return /^no\b/i.test(v.trim()) ? '' : v;
  };

  /** Field center markings on/off from its card's flat select. */
  BundleStudio.prototype.markingsOn = function () {
    var card = this.cardOfType('fieldcenter');
    var flat = card && card.querySelector('[data-bundle-variant-select]');
    var opt = flat && flat.options[flat.selectedIndex];
    if (!opt || !opt.value) return true;    // nothing picked yet: show them
    var name = String(opt.dataset.variantName || opt.textContent || '');
    return !/\bno\b/i.test(name);
  };

  /** Dimensions for one item right now (sleeves vary with Card Size). */
  BundleStudio.prototype.dims = function (type) {
    if (type === 'sleeves') {
      var s = this.sleeveSize();
      return { w: s.w, h: s.h };
    }
    return { w: ITEMS[type].w, h: ITEMS[type].h };
  };

  // ------------------------------------------------------------- rendering

  /**
   * Paints one item's PRINT SURFACE into ctx at w x h px. The same routine
   * paints the 2D design views, the 3D texture canvases and the print-scale
   * captures, so they always agree.
   */
  BundleStudio.prototype.drawItem = function (type, ctx, w, h, opts) {
    opts = opts || {};
    var item = ITEMS[type];
    var st = this.state[type];
    var d = this.dims(type);
    var pxPerMM = w / d.w;

    ctx.clearRect(0, 0, w, h);
    ctx.save();
    if (type === 'deckbox') lidPath(ctx, pxPerMM);
    else roundedRectPath(ctx, 0, 0, w, h, item.corner * pxPerMM);
    ctx.clip();

    // base surface
    if (type === 'fieldcenter') {
      var base = this.image('fc-base', this.fcBaseUrl);
      if (base) ctx.drawImage(base, 0, 0, w, h);
      else { ctx.fillStyle = '#d9b559'; ctx.fillRect(0, 0, w, h); }
    } else if (type === 'deckbox') {
      ctx.fillStyle = this.boxColor() === 'white' ? '#e8e8ec' : '#15161c';
      ctx.fillRect(0, 0, w, h);
    } else {
      ctx.fillStyle = '#15161c';
      ctx.fillRect(0, 0, w, h);
    }

    // artwork, cover-fit with this item's transform
    var art = this.art;
    if (art.img) {
      var iw = art.img.naturalWidth || art.img.width;
      var ih = art.img.naturalHeight || art.img.height;
      var k = coverFactor(iw, ih, w, h, st.rot) * st.scale;
      ctx.save();
      ctx.imageSmoothingQuality = 'high';
      ctx.translate(w / 2 + st.tx * pxPerMM, h / 2 + st.ty * pxPerMM);
      ctx.rotate(st.rot);
      ctx.drawImage(art.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
      ctx.restore();
      st.lastCover = k;
    } else {
      st.lastCover = null;
    }

    // per-item furniture, over the art exactly as printed
    if (type === 'playmat') {
      var zv = this.zoneValue();
      var zimg = zv && this.zoneMap[zv] ? this.image('zone-' + zv, this.zoneMap[zv]) : null;
      if (zimg) {
        var tz = this.tinted('zone-' + zv, zimg, this.opts.zoneColor);
        if (tz) ctx.drawImage(tz, 0, 0, w, h);
      }
    }
    if (type === 'fieldcenter' && this.markingsOn()) {
      var ov = this.fcOverlay();
      if (ov) ctx.drawImage(ov, 0, 0, w, h);
    }
    if (type === 'sleeves' && item.border) {
      var bw = item.border * pxPerMM;
      ctx.fillStyle = '#0c0c0c';
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.rect(bw, bw, w - 2 * bw, h - 2 * bw);
      ctx.fill('evenodd');
    }
    ctx.restore();

    // guides on the interactive views and the order capture, never on the
    // 3D texture canvases
    if (opts.guides) {
      var lw = opts.lw || 1;
      ctx.save();
      ctx.lineWidth = lw;
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      if (type === 'deckbox') {
        lidPath(ctx, pxPerMM);
        ctx.stroke();
        // crease centre-lines between the three faces + stitch line
        ctx.setLineDash([5 * lw, 4 * lw]);
        ctx.strokeStyle = 'rgba(255,255,255,0.45)';
        var x1 = LID_SPEC.panels[0] * pxPerMM;
        var x2 = (LID_SPEC.panels[0] + LID_SPEC.panels[1]) * pxPerMM;
        ctx.beginPath();
        ctx.moveTo(x1, 0); ctx.lineTo(x1, h);
        ctx.moveTo(x2, 0); ctx.lineTo(x2, h);
        ctx.stroke();
      } else {
        roundedRectPath(ctx, lw / 2, lw / 2, w - lw, h - lw, item.corner * pxPerMM);
        ctx.stroke();
        if (type === 'playmat' && item.safe) {
          var inset = item.safe * pxPerMM;
          ctx.setLineDash([5 * lw, 4 * lw]);
          ctx.strokeStyle = 'rgba(255,255,255,0.5)';
          ctx.strokeRect(inset, inset, w - 2 * inset, h - 2 * inset);
        }
      }
      ctx.restore();
    }
  };

  /** Markings overlay in the current colour: shipped white/black PNGs, or
   *  the white PNG as an alpha mask refilled with a custom colour. */
  BundleStudio.prototype.fcOverlay = function () {
    var c = this.opts.markColor;
    if (c === 'white') return this.image('fc-ov-white', this.fcOverlayWhiteUrl);
    if (c === 'black') return this.image('fc-ov-black', this.fcOverlayBlackUrl);
    var mask = this.image('fc-ov-white', this.fcOverlayWhiteUrl);
    return this.tinted('fc-ov', mask, c);
  };

  /** One product pane's 2D design view. */
  BundleStudio.prototype.renderView = function (type) {
    var pane = this.pane(type);
    if (!pane) return;
    var view = pane.querySelector('[data-bst-view]');
    if (!view) return;
    var d = this.dims(type);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var boxW = view.clientWidth || 520;
    var boxH = Math.round(boxW * (d.h / d.w));
    if (view.width !== Math.round(boxW * dpr) || view.height !== Math.round(boxH * dpr)) {
      view.width = Math.round(boxW * dpr);
      view.height = Math.round(boxH * dpr);
    }
    view.style.height = boxH + 'px';
    var ctx = view.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawItem(type, ctx, boxW, boxH, { guides: true });

    var empty = pane.querySelector('[data-bst-empty]');
    if (empty) empty.hidden = !!this.art.img;
    this.updateReadouts(type, pane, boxW);
  };

  BundleStudio.prototype.updateReadouts = function (type, pane, viewW) {
    var finfo = pane.querySelector('[data-bst-finfo]');
    var dpi = pane.querySelector('[data-bst-ppi]');
    if (finfo) {
      finfo.textContent = this.art.file
        ? this.art.file.name + ' — ' + bytesToLabel(this.art.file.size) : '';
    }
    if (!dpi) return;
    var st = this.state[type];
    if (this.art.img && st.lastCover) {
      var d = this.dims(type);
      var pxPerMM = (viewW || 520) / d.w;
      var ppi = (MM_PER_INCH * pxPerMM) / st.lastCover * (this.art.srcScale || 1);
      var low = ppi < (LOW_PPI[type] || 150);
      dpi.textContent = '· ' + Math.round(ppi) + ' PPI at this size' +
        (low ? ' — aim for ' + (LOW_PPI[type] || 150) + '+' : '');
      dpi.classList.toggle('bst-warn', low);
      dpi.hidden = false;
    } else {
      dpi.hidden = true;
    }
  };

  BundleStudio.prototype.pane = function (type) {
    for (var i = 0; i < this.el.panes.length; i++) {
      if (this.el.panes[i].getAttribute('data-bst-pane') === type) return this.el.panes[i];
    }
    return null;
  };

  BundleStudio.prototype.renderAll = function () {
    for (var i = 0; i < TYPE_ORDER.length; i++) this.renderView(TYPE_ORDER[i]);
    this.update3D();
    this.scheduleCardShots();
  };

  // ---------------------------------------------------------------- upload

  BundleStudio.prototype.loadFile = function (file) {
    var self = this;
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      return this.say('That file is not an image. Please upload a PNG or JPG.', true);
    }
    if (file.size > this.maxBytes) {
      return this.say('That file is ' + bytesToLabel(file.size) + '. Please keep it under ' +
        bytesToLabel(this.maxBytes) + '.', true);
    }
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      var MAXDIM = 2600;
      var big = Math.max(img.naturalWidth, img.naturalHeight);
      if (big > MAXDIM) {
        var r = MAXDIM / big;
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * r);
        c.height = Math.round(img.naturalHeight * r);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var small = new Image();
        small.onload = function () {
          URL.revokeObjectURL(url);
          self.adopt(small, file, 1 / r);
        };
        small.src = c.toDataURL('image/jpeg', 0.92);
      } else {
        self.adopt(img, file, 1);
      }
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('That image could not be read. Try re-saving it as a PNG or JPG.', true);
    };
    img.src = url;
  };

  BundleStudio.prototype.adopt = function (img, file, srcScale) {
    this.art = { img: img, file: file, srcScale: srcScale || 1 };
    for (var i = 0; i < TYPE_ORDER.length; i++) {
      var st = this.state[TYPE_ORDER[i]];
      st.tx = 0; st.ty = 0; st.scale = 1; st.rot = 0;
    }
    this.syncSliders();
    if (this.el.artThumb) this.el.artThumb.src = img.src;
    if (this.el.filename) this.el.filename.textContent = file.name + ' — ' + bytesToLabel(file.size);
    this.root.classList.add('bst--has-art');
    this.say('');
    this.ensureStages();        // card thumbs go live from the moment of upload
    this.renderAll();
    this.updateGate();
  };

  BundleStudio.prototype.clearArt = function () {
    this.art = { img: null, file: null, srcScale: 1 };
    for (var i = 0; i < TYPE_ORDER.length; i++) {
      var st = this.state[TYPE_ORDER[i]];
      st.tx = 0; st.ty = 0; st.scale = 1; st.rot = 0;
    }
    this.syncSliders();
    if (this.el.fileInput) this.el.fileInput.value = '';
    if (this.el.filename) this.el.filename.textContent = '';
    this.root.classList.remove('bst--has-art');
    this.renderAll();
    this.updateGate();
  };

  BundleStudio.prototype.syncSliders = function () {
    for (var i = 0; i < TYPE_ORDER.length; i++) {
      var type = TYPE_ORDER[i];
      var pane = this.pane(type);
      if (!pane) continue;
      var st = this.state[type];
      var z = pane.querySelector('[data-bst-zoom]');
      var r = pane.querySelector('[data-bst-rot]');
      if (z) z.value = String(Math.round(st.scale * 100));
      if (r) r.value = String(Math.round(st.rot * 180 / Math.PI));
    }
  };

  BundleStudio.prototype.say = function (msg, isError) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('bst-warn', !!isError);
    this.el.status.hidden = !msg;
  };

  // ------------------------------------------------------------------ tabs

  BundleStudio.prototype.selectTab = function (name) {
    this.selected = name;
    for (var i = 0; i < this.el.tabs.length; i++) {
      var t = this.el.tabs[i];
      var on = t.getAttribute('data-bst-tab') === name;
      t.classList.toggle('bst-tab--on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (var j = 0; j < this.el.panes.length; j++) {
      var p = this.el.panes[j];
      p.classList.toggle('bst-pane--on', p.getAttribute('data-bst-pane') === name);
    }
    if (ITEMS[name]) {
      this.ensureStages();
      this.renderView(name);
      var rec = this.stages[name];
      if (rec) {
        // every visit opens on the printed side and auto-rotates from there
        if (rec.homePose) {
          rec.group.rotation.y = rec.homePose.y;
          rec.group.rotation.x = rec.homePose.x;
        }
        rec.spin = true;
        this.syncSpinButton(rec);
        rec.needsRender = true;
        if (rec.resize) rec.resize();
      }
    }
  };

  BundleStudio.prototype.bindTabs = function () {
    var self = this;
    for (var i = 0; i < this.el.tabs.length; i++) {
      (function (tab) {
        tab.addEventListener('click', function () {
          self.selectTab(tab.getAttribute('data-bst-tab'));
        });
      })(this.el.tabs[i]);
    }
  };

  BundleStudio.prototype.bindUpload = function () {
    var self = this;
    var el = this.el;

    if (el.fileInput) {
      el.fileInput.addEventListener('change', function () {
        self.loadFile(this.files && this.files[0]);
      });
    }
    if (el.dropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.add('bst-drop-hot');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.remove('bst-drop-hot');
        });
      });
      el.dropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) self.loadFile(e.dataTransfer.files[0]);
      });
    }

    // Remove only via the confirmation modal (Upload tab only)
    if (el.remove) {
      el.remove.addEventListener('click', function () {
        if (el.modal) el.modal.classList.add('bst-modal--on');
        else self.clearArt();
      });
    }
    if (el.modalKeep) {
      el.modalKeep.addEventListener('click', function () {
        el.modal.classList.remove('bst-modal--on');
      });
    }
    if (el.modalRemove) {
      el.modalRemove.addEventListener('click', function () {
        el.modal.classList.remove('bst-modal--on');
        self.clearArt();
      });
    }
    if (el.modal) {
      el.modal.addEventListener('click', function (e) {
        if (e.target === el.modal) el.modal.classList.remove('bst-modal--on');
      });
    }
  };

  // ------------------------------------------- per-pane design interaction

  BundleStudio.prototype.bindPanes = function () {
    var self = this;
    TYPE_ORDER.forEach(function (type) {
      var pane = self.pane(type);
      if (!pane) return;
      var st = self.state[type];
      var zoom = pane.querySelector('[data-bst-zoom]');
      var rot = pane.querySelector('[data-bst-rot]');
      var reset = pane.querySelector('[data-bst-reset]');
      var view = pane.querySelector('[data-bst-view]');

      if (zoom) zoom.addEventListener('input', function () {
        st.scale = clamp(parseFloat(this.value) / 100, 0.25, 4);
        self.renderAll();
      });
      if (rot) rot.addEventListener('input', function () {
        st.rot = parseFloat(this.value) * Math.PI / 180;
        self.renderAll();
      });
      if (reset) reset.addEventListener('click', function () {
        st.tx = 0; st.ty = 0; st.scale = 1; st.rot = 0;
        if (zoom) zoom.value = '100';
        if (rot) rot.value = '0';
        self.renderAll();
      });

      if (view) {
        var last = null;
        var pointers = {};
        var pinchBase = null, pinchScale = 1;
        view.addEventListener('pointerdown', function (e) {
          if (!self.art.img) return;
          view.setPointerCapture(e.pointerId);
          pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
          last = { x: e.clientX, y: e.clientY };
        });
        view.addEventListener('pointermove', function (e) {
          if (!self.art.img || !pointers[e.pointerId]) return;
          pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
          var ids = Object.keys(pointers);
          if (ids.length >= 2) {
            var a = pointers[ids[0]], b = pointers[ids[1]];
            var dist = Math.hypot(a.x - b.x, a.y - b.y);
            if (pinchBase) {
              st.scale = clamp(pinchScale * (dist / pinchBase), 0.25, 4);
              if (zoom) zoom.value = String(Math.round(st.scale * 100));
            } else { pinchBase = dist; pinchScale = st.scale; }
            self.renderAll();
            return;
          }
          var d = self.dims(type);
          var mmPerPx = d.w / (view.clientWidth || 520);
          st.tx = clamp(st.tx + (e.clientX - last.x) * mmPerPx, -d.w, d.w);
          st.ty = clamp(st.ty + (e.clientY - last.y) * mmPerPx, -d.h, d.h);
          last = { x: e.clientX, y: e.clientY };
          self.renderAll();
        });
        ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
          view.addEventListener(evt, function (e) {
            delete pointers[e.pointerId];
            if (!Object.keys(pointers).length) { pinchBase = null; }
          });
        });
        view.addEventListener('wheel', function (e) {
          if (!self.art.img) return;
          e.preventDefault();
          st.scale = clamp(st.scale * (e.deltaY < 0 ? 1.06 : 0.94), 0.25, 4);
          if (zoom) zoom.value = String(Math.round(st.scale * 100));
          self.renderAll();
        }, { passive: false });
        view.addEventListener('dblclick', function () {
          if (!self.art.img) return;
          st.tx = 0; st.ty = 0; st.scale = 1; st.rot = 0;
          if (zoom) zoom.value = '100';
          if (rot) rot.value = '0';
          self.renderAll();
        });
      }

      // playmat preview pose
      pane.querySelectorAll('[data-bst-matview]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.matView = btn.getAttribute('data-bst-matview');
          pane.querySelectorAll('[data-bst-matview]').forEach(function (b) {
            b.classList.toggle('bst-seg--on', b === btn);
          });
          self.update3D();
        });
      });

      // auto-rotate toggles + fc 3D zoomers
      var spin = pane.querySelector('[data-bst-spin]');
      if (spin) spin.addEventListener('click', function () {
        var rec = self.stages[type];
        if (!rec) return;
        rec.spin = !rec.spin;
        spin.classList.toggle('bst-seg--on', rec.spin);
        spin.setAttribute('aria-pressed', rec.spin ? 'true' : 'false');
      });
      var zin = pane.querySelector('[data-bst-z3in]');
      var zout = pane.querySelector('[data-bst-z3out]');
      if (zin) zin.addEventListener('click', function () {
        var rec = self.stages[type];
        if (rec && rec.nudgeZoom) rec.nudgeZoom(1.2);
      });
      if (zout) zout.addEventListener('click', function () {
        var rec = self.stages[type];
        if (rec && rec.nudgeZoom) rec.nudgeZoom(1 / 1.2);
      });
    });

    var self2 = this;
    var ro = window.ResizeObserver ? new ResizeObserver(function () {
      self2.renderAll();
      for (var t in self2.stages) {
        if (self2.stages[t] && self2.stages[t].resize) self2.stages[t].resize();
      }
    }) : null;
    if (ro) ro.observe(this.root);
  };

  // --------------------------------------------------- option mirrors
  //
  // Each product tab exposes ONLY its agreed option, as a mirror of the
  // bundle card's own control. The card select stays the source of truth:
  // changing a mirror writes the card select and dispatches 'change' (so
  // bundle-savings.js maps it onto Metro's flat variant select and gates
  // the CTA), and changing the card select updates the mirror.

  BundleStudio.prototype.buildOptionMirrors = function () {
    this.applyCardDefaults();
    this.buildZoneMirror();
    this.buildBoxColorMirror();
    this.buildSleeveSizeMirror();
    this.buildMarkColorSwatches();
    this.updateMatBadges();
  };

  /**
   * Opinionated defaults on the cards themselves (Mert 2026-10-01): the deck
   * box opens on Black and the field center on "Add Field Center Design"
   * (markings on, white), instead of an unanswered "Select...". Only fills
   * a placeholder — a choice already made (e.g. restored by Metro) is kept.
   */
  BundleStudio.prototype.applyCardDefaults = function () {
    function pick(flat, want, avoid) {
      if (!flat || flat.value) return;
      for (var i = 0; i < flat.options.length; i++) {
        var o = flat.options[i];
        if (!o.value || o.disabled) continue;
        var name = String(o.dataset.variantName || o.textContent || '');
        if (want.test(name) && !(avoid && avoid.test(name))) {
          flat.value = o.value;
          flat.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
    var db = this.cardOfType('deckbox');
    pick(db && db.querySelector('[data-bundle-variant-select]'), /black/i, null);
    var fc = this.cardOfType('fieldcenter');
    pick(fc && fc.querySelector('[data-bundle-variant-select]'), /add/i, /\bno\b/i);

    // sleeves (multi-option card): Card Size -> Standard, Finish Style ->
    // the gloss-with-black finish. Sets the card's own option selects and
    // lets bundle-savings.js map them onto the flat variant select.
    function pickOpt(sel, primary, fallback) {
      if (!sel || sel.value) return;
      var match = null;
      for (var i = 0; i < sel.options.length && !match; i++) {
        var o = sel.options[i];
        if (o.value && !o.disabled && primary.test(o.value)) match = o;
      }
      for (var j = 0; j < sel.options.length && !match && fallback; j++) {
        var o2 = sel.options[j];
        if (o2.value && !o2.disabled && fallback.test(o2.value)) match = o2;
      }
      if (match) {
        sel.value = match.value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    var sl = this.cardOfType('sleeves');
    pickOpt(this.cardOptionSelect(sl, this.optNames.cardsize), /^standard/i, null);
    pickOpt(this.cardOptionSelect(sl, 'Finish Style'), /gloss.*black/i, /gloss/i);
  };

  BundleStudio.prototype.optionsHost = function (type) {
    var pane = this.pane(type);
    return pane ? pane.querySelector('[data-bst-opts]') : null;
  };

  BundleStudio.prototype.labelled = function (host, text) {
    var lab = document.createElement('p');
    lab.className = 'bst-opt-label';
    lab.textContent = text;
    host.appendChild(lab);
    return lab;
  };

  /** Playmat: playzone dropdown mirroring the card's (already game-filtered). */
  BundleStudio.prototype.buildZoneMirror = function () {
    var host = this.optionsHost('playmat');
    var card = this.cardOfType('playmat');
    var src = this.cardOptionSelect(card, this.optNames.zone);
    if (!host || !src) return;
    host.textContent = '';
    this.labelled(host, 'Add Playzones');
    var sel = document.createElement('select');
    sel.className = 'bst-select';
    sel.setAttribute('data-bst-zone-select', '');
    sel.setAttribute('aria-label', 'Add Playzones');
    for (var i = 0; i < src.options.length; i++) {
      var o = src.options[i];
      var copy = document.createElement('option');
      copy.value = o.value;
      copy.textContent = o.textContent.trim();
      copy.disabled = o.disabled;
      host.ownerDocument && sel.appendChild(copy);
    }
    sel.value = src.value;
    var self = this;
    sel.addEventListener('change', function () {
      src.value = sel.value;
      src.dispatchEvent(new Event('change', { bubbles: true }));
      self.renderAll();
    });
    src.addEventListener('change', function () {
      if (sel.value !== src.value) sel.value = src.value;
      self.renderAll();
    });
    host.appendChild(sel);
  };

  /** Deck box: Black / White pills mirroring its flat variant select. */
  BundleStudio.prototype.buildBoxColorMirror = function () {
    var host = this.optionsHost('deckbox');
    var card = this.cardOfType('deckbox');
    var flat = card && card.querySelector('[data-bundle-variant-select]');
    if (!host || !flat) return;
    host.textContent = '';
    this.labelled(host, 'Color');
    var row = document.createElement('div');
    row.className = 'bst-pills';
    var self = this;
    var pills = [];
    for (var i = 0; i < flat.options.length; i++) {
      (function (o) {
        if (!o.value) return;
        var name = String(o.dataset.variantName || o.textContent || '').trim();
        var short = name.split('(')[0].trim() || name;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bst-pill';
        b.textContent = short;
        b.disabled = o.disabled;
        b.setAttribute('data-bst-boxcol', /white/i.test(name) ? 'white' : 'black');
        b.addEventListener('click', function () {
          flat.value = o.value;
          flat.dispatchEvent(new Event('change', { bubbles: true }));
        });
        pills.push({ btn: b, value: o.value });
        row.appendChild(b);
      })(flat.options[i]);
    }
    var sync = function () {
      for (var j = 0; j < pills.length; j++) {
        pills[j].btn.classList.toggle('bst-pill--on', flat.value === pills[j].value);
      }
      self.renderAll();
    };
    flat.addEventListener('change', sync);
    sync();
    host.appendChild(row);
  };

  /** Sleeves: Card Size pills mirroring the card's Card Size select. */
  BundleStudio.prototype.buildSleeveSizeMirror = function () {
    var host = this.optionsHost('sleeves');
    var card = this.cardOfType('sleeves');
    var src = this.cardOptionSelect(card, this.optNames.cardsize);
    if (!host || !src) return;
    host.textContent = '';
    this.labelled(host, 'Card Size');
    var row = document.createElement('div');
    row.className = 'bst-pills';
    var self = this;
    var pills = [];
    for (var i = 0; i < src.options.length; i++) {
      (function (o) {
        if (!o.value) return;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bst-pill';
        b.textContent = o.value;
        b.disabled = o.disabled;
        b.addEventListener('click', function () {
          src.value = o.value;
          src.dispatchEvent(new Event('change', { bubbles: true }));
        });
        pills.push({ btn: b, value: o.value });
        row.appendChild(b);
      })(src.options[i]);
    }
    var sync = function () {
      for (var j = 0; j < pills.length; j++) {
        pills[j].btn.classList.toggle('bst-pill--on', src.value === pills[j].value);
      }
      self.rebuildSleeveGeometry();
      self.renderAll();
    };
    src.addEventListener('change', sync);
    sync();
    host.appendChild(row);
  };

  /**
   * Field center: markings colour — white, black, then the customer's own
   * picks. Up to 5 custom swatches accumulate before the oldest is replaced
   * (same behaviour as the standalone field center tool). A design-layer
   * choice: it rides in properties[_design], not in the variant.
   */
  BundleStudio.prototype.buildMarkColorSwatches = function () {
    var host = this.optionsHost('fieldcenter');
    if (!host) return;
    host.textContent = '';
    this.labelled(host, 'Markings colour');
    var row = document.createElement('div');
    row.className = 'bst-swatches';
    row.setAttribute('data-bst-swatches', '');
    host.appendChild(row);

    var input = document.createElement('input');
    input.type = 'color';
    input.value = '#ff5f5f';
    input.className = 'bst-color-input';
    input.setAttribute('data-bst-mark-input', '');
    host.appendChild(input);

    var self = this;
    input.addEventListener('input', function () {
      self.opts.markColor = String(input.value).toLowerCase();
      self.renderSwatches();
      self.renderAll();
    });
    input.addEventListener('change', function () {
      var hex = String(input.value).toLowerCase();
      self.opts.markColor = hex;
      var list = self.opts.customColors;
      if (list.indexOf(hex) === -1) {
        list.push(hex);
        if (list.length > 5) list.shift();
      }
      self.renderSwatches();
      self.renderAll();
    });
    this.renderSwatches();
  };

  BundleStudio.prototype.renderSwatches = function () {
    var host = this.optionsHost('fieldcenter');
    var row = host && host.querySelector('[data-bst-swatches]');
    var input = host && host.querySelector('[data-bst-mark-input]');
    if (!row) return;
    row.textContent = '';
    var self = this;
    function addSwatch(key, css, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bst-sw';
      b.style.background = css;
      b.setAttribute('aria-label', label);
      b.setAttribute('data-bst-mark', key);
      b.classList.toggle('bst-sw--on', self.opts.markColor === key);
      b.addEventListener('click', function () {
        self.opts.markColor = key;
        self.renderSwatches();
        self.renderAll();
      });
      row.appendChild(b);
    }
    addSwatch('white', '#f5f5f2', 'Markings white');
    addSwatch('black', '#101013', 'Markings black');
    for (var i = 0; i < this.opts.customColors.length; i++) {
      addSwatch(this.opts.customColors[i], this.opts.customColors[i],
        'Markings ' + this.opts.customColors[i]);
    }
    // the picker swatch opens the native colour input
    var pick = document.createElement('button');
    pick.type = 'button';
    pick.className = 'bst-sw bst-sw--picker';
    pick.setAttribute('aria-label', 'Pick any markings colour');
    pick.setAttribute('data-bst-mark-picker', '');
    var previewing = this.opts.markColor.charAt(0) === '#' &&
      this.opts.customColors.indexOf(this.opts.markColor) === -1;
    if (previewing) {
      pick.style.background = this.opts.markColor;
      pick.classList.add('bst-sw--on');
    }
    pick.addEventListener('click', function () { if (input) input.click(); });
    row.appendChild(pick);
  };

  /** Playmat badges mirror the card's stitching/thickness pick, when made. */
  BundleStudio.prototype.updateMatBadges = function () {
    var pane = this.pane('playmat');
    var badges = pane && pane.querySelector('[data-bst-badges]');
    if (!badges) return;
    var card = this.cardOfType('playmat');
    var sel = this.cardOptionSelect(card, this.optNames.matfinish);
    var v = sel ? String(sel.value || '') : '';
    badges.textContent = '';
    var parts = [];
    if (/stitch/i.test(v) && !/no\s*stitch/i.test(v)) parts.push('STITCHED EDGE');
    else parts.push('NO STITCHING');
    parts.push(/3\s*mm/i.test(v) ? '3 MM' : '2 MM');
    for (var i = 0; i < parts.length; i++) {
      var b = document.createElement('span');
      b.className = 'bst-badge';
      b.textContent = parts[i];
      badges.appendChild(b);
    }
  };

  // --------------------------------------------- live product-card thumbs

  /** Injects a live-preview layer into each custom product card's media. */
  BundleStudio.prototype.bindCardLive = function () {
    var self = this;
    this.cardLive = {};
    TYPE_ORDER.forEach(function (type) {
      var card = self.cardOfType(type);
      if (!card) return;
      var media = card.querySelector('.product-card-media');
      if (!media || media.querySelector('.bst-live')) return;
      var box = document.createElement('div');
      box.className = 'bst-live' + (type === 'playmat' ? ' bst-live--mat' : '');
      var img = document.createElement('img');
      img.alt = ITEMS[type].label + ' live preview';
      box.appendChild(img);
      var tag = document.createElement('span');
      tag.className = 'bst-live-tag';
      tag.textContent = 'LIVE PREVIEW';
      box.appendChild(tag);
      media.appendChild(box);
      // the product photo stays until the first snapshot is actually in
      img.addEventListener('load', function () { box.classList.add('bst-live--ready'); });
      self.cardLive[type] = img;
    });
  };

  /** Debounced snapshots of each preview into its card thumbnail. */
  BundleStudio.prototype.scheduleCardShots = function () {
    if (!this.art.img || !this.cardLive) return;
    var self = this;
    clearTimeout(this._shotTimer);
    this._shotTimer = setTimeout(function () { self.takeCardShots(); }, 280);
  };

  BundleStudio.prototype.takeCardShots = function () {
    if (!this.art.img || !this.cardLive) return;
    for (var type in this.cardLive) {
      var img = this.cardLive[type];
      if (!img) continue;
      try {
        if (type === 'playmat') {
          // flat print at card scale; the tilt is CSS on the img
          var d = this.dims(type);
          var c = document.createElement('canvas');
          c.width = 420; c.height = Math.round(420 * d.h / d.w);
          this.drawItem(type, c.getContext('2d'), c.width, c.height, {});
          img.src = c.toDataURL('image/jpeg', 0.85);
        } else {
          var rec = this.stages[type];
          if (rec && rec.renderer) {
            // snapshot from the canonical card pose (the deck box turns to
            // show its printed wrap faces), whatever the user's orbit or the
            // auto-rotation is doing, then put the view back
            var pose = rec.cardPose || rec.homePose;
            var ry = rec.group.rotation.y, rx = rec.group.rotation.x;
            rec.group.rotation.y = pose ? pose.y : ry;
            rec.group.rotation.x = pose ? pose.x : rx;
            rec.render();
            img.src = rec.renderer.domElement.toDataURL('image/png');
            rec.group.rotation.y = ry;
            rec.group.rotation.x = rx;
            rec.needsRender = true;
          } else {
            // 3D not up (yet / unsupported): fall back to the flat print
            var d2 = this.dims(type);
            var c2 = document.createElement('canvas');
            c2.width = 300; c2.height = Math.round(300 * d2.h / d2.w);
            this.drawItem(type, c2.getContext('2d'), c2.width, c2.height, {});
            img.src = c2.toDataURL('image/jpeg', 0.85);
          }
        }
      } catch (e) { /* tainted canvas or similar: leave the product photo */ }
    }
  };

  // ------------------------------------------------------------------- 3D
  //
  // Engine notes carried over from the field center / sleeve tools:
  //   - three.js loads as a CLASSIC script (window.THREE). Dynamic import()
  //     of the ESM build broke in production (an app bundler patches it).
  //   - ACES tone mapping + lifted exposure, and the print textures double
  //     as emissiveMap so the artwork keeps the flat editor's brightness.
  //   - tight near/far around the object for depth precision.

  function loadThree(url, cb) {
    if (window.THREE) return cb(window.THREE);
    var existing = document.querySelector('script[data-bst-three], script[data-fc-three], script[data-sd-three]');
    if (existing) {
      existing.addEventListener('load', function () { cb(window.THREE || null, 'script loaded but no global'); });
      existing.addEventListener('error', function () { cb(null, 'script tag failed'); });
      return;
    }
    if (!url) return cb(null, 'empty asset url');
    var sc = document.createElement('script');
    sc.src = url;
    sc.async = true;
    sc.setAttribute('data-bst-three', '');
    sc.onload = function () { cb(window.THREE || null, window.THREE ? null : 'script loaded but no global'); };
    sc.onerror = function () { cb(null, 'script tag failed'); };
    document.head.appendChild(sc);
  }

  /** Builds every 3D stage once (first upload or first product tab). */
  BundleStudio.prototype.ensureStages = function () {
    var self = this;
    if (this._stagesStarted) return;
    this._stagesStarted = true;
    loadThree(this.threeUrl, function (THREE, why) {
      if (!THREE) {
        console.error('[bundle-studio] three.js did not load (' + (why || 'unknown') + ')');
        self.fail3D();
        return;
      }
      self.THREE = THREE;
      try {
        self.buildStage('deckbox');
        self.buildStage('sleeves');
        self.buildStage('fieldcenter');
        self.update3D();
        self.scheduleCardShots();
      } catch (err) {
        console.error('[bundle-studio] 3D scenes failed to build', err);
        self.fail3D();
      }
    });
  };

  /** Visible, explained degradation — the flat views still carry the order. */
  BundleStudio.prototype.fail3D = function () {
    this.root.classList.add('bst--3d-off');
    var types = ['deckbox', 'sleeves', 'fieldcenter'];
    for (var i = 0; i < types.length; i++) {
      var pane = this.pane(types[i]);
      var stage = pane && pane.querySelector('[data-bst-stage]');
      if (stage && !stage.querySelector('.bst-3d-error')) {
        var p = document.createElement('p');
        p.className = 'bst-3d-error';
        p.textContent = '3D preview is not available in this browser. Your design and ' +
          'the placement are still saved with your order.';
        stage.appendChild(p);
      }
    }
  };

  /** Renderer + scene + lights + env, the shared recipe. */
  BundleStudio.prototype.sceneSetup = function (stage, exposure) {
    var THREE = this.THREE;
    var renderer = new THREE.WebGLRenderer({
      antialias: true, alpha: true, preserveDrawingBuffer: true
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = exposure;
    stage.appendChild(renderer.domElement);

    var scene = new THREE.Scene();

    var envCanvas = document.createElement('canvas');
    envCanvas.width = 32; envCanvas.height = 128;
    var ectx = envCanvas.getContext('2d');
    var grad = ectx.createLinearGradient(0, 0, 0, 128);
    grad.addColorStop(0.00, '#ffffff');
    grad.addColorStop(0.40, '#dbe3ee');
    grad.addColorStop(0.52, '#7f8798');
    grad.addColorStop(1.00, '#20242c');
    ectx.fillStyle = grad; ectx.fillRect(0, 0, 32, 128);
    var envTex = new THREE.CanvasTexture(envCanvas);
    envTex.mapping = THREE.EquirectangularReflectionMapping;
    var pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromEquirectangular(envTex).texture;
    envTex.dispose();

    // softbox band for a visible sweep on gloss/metal faces
    var glossCanvas = document.createElement('canvas');
    glossCanvas.width = 256; glossCanvas.height = 128;
    var gctx = glossCanvas.getContext('2d');
    gctx.fillStyle = '#23262e';
    gctx.fillRect(0, 0, 256, 128);
    var band = gctx.createLinearGradient(0, 20, 0, 78);
    band.addColorStop(0, 'rgba(255,255,255,0)');
    band.addColorStop(0.5, 'rgba(255,255,255,0.95)');
    band.addColorStop(1, 'rgba(255,255,255,0)');
    gctx.fillStyle = band;
    gctx.save();
    gctx.translate(128, 48); gctx.rotate(-0.18);
    gctx.fillRect(-140, -30, 280, 60);
    gctx.restore();
    var glossTex = new THREE.CanvasTexture(glossCanvas);
    glossTex.mapping = THREE.EquirectangularReflectionMapping;
    var envGloss = pmrem.fromEquirectangular(glossTex).texture;
    glossTex.dispose();
    pmrem.dispose();

    scene.add(new THREE.HemisphereLight(0xffffff, 0x404450, 0.55));
    var key = new THREE.DirectionalLight(0xffffff, 2.0);
    key.position.set(-120, 180, 240); scene.add(key);
    var fill = new THREE.DirectionalLight(0xdfe8ff, 0.7);
    fill.position.set(180, -60, 160); scene.add(fill);
    var rim = new THREE.DirectionalLight(0xffffff, 1.1);
    rim.position.set(60, 40, -220); scene.add(rim);

    return { renderer: renderer, scene: scene, envGloss: envGloss };
  };

  /** CanvasTexture over an offscreen canvas the drawItem pipeline paints. */
  BundleStudio.prototype.canvasTex = function (renderer, canvas) {
    var THREE = this.THREE;
    var tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    return tex;
  };

  BundleStudio.prototype.buildStage = function (type) {
    var pane = this.pane(type);
    var stage = pane && pane.querySelector('[data-bst-stage]');
    if (!stage || this.stages[type]) return;
    var THREE = this.THREE;

    var base = this.sceneSetup(stage, type === 'fieldcenter' ? 1.10 : 1.04);
    var rec = {
      type: type,
      renderer: base.renderer,
      scene: base.scene,
      envGloss: base.envGloss,
      stage: stage,
      group: new THREE.Group(),
      spin: true,
      zoom3D: 1,
      needsRender: true
    };
    base.scene.add(rec.group);
    this.stages[type] = rec;

    if (type === 'deckbox') this.buildDeckboxMeshes(rec);
    else if (type === 'sleeves') this.buildSleeveMeshes(rec);
    else if (type === 'fieldcenter') this.buildFieldcenterMeshes(rec);

    rec.camera = new THREE.PerspectiveCamera(28, 1, rec.near, rec.far);
    rec.camera.position.set(0, 0, rec.far * 0.6);
    // opening pose: deck box turned so a printed side face shows beside the
    // drawer fronts (the product-photo angle); flat items face the viewer
    // with their printed side toward the camera
    rec.homePose = type === 'deckbox' ? { y: 0.55, x: 0.12 } : { y: -0.26, x: 0.07 };
    // the product-card thumbnail pose: the deck box turns further so its
    // printed back + side wrap faces fill the shot (Mert's reference)
    if (type === 'deckbox') rec.cardPose = { y: 2.65, x: 0.10 };
    rec.group.rotation.y = rec.homePose.y;
    rec.group.rotation.x = rec.homePose.x;

    var self = this;
    rec.resize = function () {
      var w = stage.clientWidth || 320;
      var h = stage.clientHeight || Math.round(w * 1.05);
      if (!w || !h) return;
      rec.renderer.setSize(w, h, false);
      var aspect = w / h;
      rec.camera.aspect = aspect;
      var need = Math.max(rec.fitH, rec.fitW / aspect) * 1.12;
      rec.baseCamZ = need / (2 * Math.tan(rec.camera.fov * Math.PI / 360));
      rec.camera.position.z = rec.baseCamZ / clamp(rec.zoom3D, 0.6, 2.6);
      rec.camera.updateProjectionMatrix();
      rec.needsRender = true;
    };
    rec.nudgeZoom = function (f) {
      rec.zoom3D = clamp((rec.zoom3D || 1) * f, 0.6, 2.6);
      rec.camera.position.z = rec.baseCamZ / rec.zoom3D;
      rec.needsRender = true;
    };
    rec.render = function () {
      rec.renderer.render(rec.scene, rec.camera);
      rec.needsRender = false;
    };

    this.bindStageOrbit(rec);
    rec.resize();
    this.startStageLoop(rec);
  };

  BundleStudio.prototype.bindStageOrbit = function (rec) {
    var self = this;
    var dom = rec.renderer.domElement;
    var drag = false, last = null;
    var pointers = {}, pinchDist = null;
    dom.style.touchAction = 'none';

    function count() { return Object.keys(pointers).length; }
    function dist() {
      var ids = Object.keys(pointers);
      var a = pointers[ids[0]], b = pointers[ids[1]];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }
    dom.addEventListener('pointerdown', function (e) {
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      drag = count() === 1;
      if (count() === 2) { drag = false; pinchDist = dist(); }
      last = { x: e.clientX, y: e.clientY };
      rec.spin = false;
      self.syncSpinButton(rec);
      dom.setPointerCapture(e.pointerId);
    });
    dom.addEventListener('pointermove', function (e) {
      if (pointers[e.pointerId]) pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (count() === 2 && pinchDist) {
        var nd = dist();
        if (nd > 0) { rec.nudgeZoom(nd / pinchDist); pinchDist = nd; }
        return;
      }
      if (!drag) return;
      var dx = e.clientX - last.x, dy = e.clientY - last.y;
      rec.group.rotation.y += dx * 0.008;
      rec.group.rotation.x = clamp(rec.group.rotation.x + dy * 0.006, -0.9, 0.9);
      last = { x: e.clientX, y: e.clientY };
      rec.needsRender = true;
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
      dom.addEventListener(evt, function (e) {
        delete pointers[e.pointerId];
        if (count() < 2) pinchDist = null;
        if (count() === 0) drag = false;
      });
    });
    dom.addEventListener('wheel', function (e) {
      e.preventDefault();
      rec.nudgeZoom(e.deltaY < 0 ? 1.08 : 1 / 1.08);
    }, { passive: false });
  };

  BundleStudio.prototype.syncSpinButton = function (rec) {
    var pane = this.pane(rec.type);
    var btn = pane && pane.querySelector('[data-bst-spin]');
    if (btn) {
      btn.classList.toggle('bst-seg--on', rec.spin);
      btn.setAttribute('aria-pressed', rec.spin ? 'true' : 'false');
    }
  };

  BundleStudio.prototype.startStageLoop = function (rec) {
    var self = this;
    function frame() {
      // only burn frames while the studio is on screen
      if (rec.spin && self.art.img) {
        rec.group.rotation.y += 0.006;
        rec.needsRender = true;
      }
      if (rec.needsRender) rec.render();
      rec.raf = requestAnimationFrame(frame);
    }
    frame();
  };

  // ---- deck box: the simple closed box (outer look only) -----------------

  BundleStudio.prototype.buildDeckboxMeshes = function (rec) {
    var THREE = this.THREE;

    // per-face texture canvases, painted by updateDeckboxTextures()
    rec.texCanvas = {
      left: document.createElement('canvas'),    // wrap panel A
      back: document.createElement('canvas'),    // wrap panel B (flipped)
      right: document.createElement('canvas'),   // wrap panel C
      front: document.createElement('canvas'),   // drawer fronts
      top: document.createElement('canvas'),
      bottom: document.createElement('canvas')
    };
    rec.texCanvas.left.width = 352; rec.texCanvas.left.height = 614;
    rec.texCanvas.right.width = 352; rec.texCanvas.right.height = 614;
    rec.texCanvas.back.width = 440; rec.texCanvas.back.height = 614;
    rec.texCanvas.front.width = 440; rec.texCanvas.front.height = 614;
    rec.texCanvas.top.width = 440; rec.texCanvas.top.height = 352;
    rec.texCanvas.bottom.width = 440; rec.texCanvas.bottom.height = 352;

    rec.tex = {};
    rec.mats = {};
    var self = this;
    ['right', 'left', 'top', 'bottom', 'front', 'back'].forEach(function (face) {
      rec.tex[face] = self.canvasTex(rec.renderer, rec.texCanvas[face]);
      rec.mats[face] = new THREE.MeshPhysicalMaterial({
        map: rec.tex[face],
        emissive: 0xffffff,
        emissiveMap: rec.tex[face],
        emissiveIntensity: 0.18,
        roughness: 0.62, metalness: 0.0,
        clearcoat: 0.25, clearcoatRoughness: 0.5,
        envMap: rec.envGloss, envMapIntensity: 0.5
      });
    });

    // BoxGeometry material order: +x, -x, +y, -y, +z, -z
    var geo = new THREE.BoxGeometry(BOX.w, BOX.h, BOX.d);
    rec.mesh = new THREE.Mesh(geo, [
      rec.mats.right, rec.mats.left, rec.mats.top,
      rec.mats.bottom, rec.mats.front, rec.mats.back
    ]);
    rec.group.add(rec.mesh);

    rec.fitW = 180; rec.fitH = 200;
    rec.near = 60; rec.far = 1600;
  };

  /**
   * Repaints the six box faces. The wrap sheet (panels A|B|C) is painted
   * once by drawItem, split per face, and the traced die-cut edge silhouette
   * is stamped on the side faces' front edges with a pale lining — the
   * signature look from the product photos. The front face is the two
   * drawer fronts: base colour, stitched seams.
   */
  BundleStudio.prototype.updateDeckboxTextures = function (rec) {
    var wrapW = 1138, wrapH = 610;        // 284.5 x 152.4mm at 4 px/mm
    if (!rec._wrap) { rec._wrap = document.createElement('canvas'); }
    var wrap = rec._wrap;
    wrap.width = wrapW; wrap.height = wrapH;
    this.drawItem('deckbox', wrap.getContext('2d'), wrapW, wrapH, {});

    var k = wrapW / LID_SPEC.w;           // px per mm on the wrap
    var xA = 0, xB = LID_SPEC.panels[0] * k, xC = (LID_SPEC.panels[0] + LID_SPEC.panels[1]) * k;
    var wSide = LID_SPEC.panels[0] * k, wBack = LID_SPEC.panels[1] * k;
    var base = this.boxColor() === 'white' ? '#e8e8ec' : '#15161c';
    var stitch = this.boxColor() === 'white' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.35)';

    function copyPanel(canvas, sx, sw, flip, notchedEdge) {
      var ctx = canvas.getContext('2d');
      var w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      // the wrap (152.4mm) is a touch shorter than the box (157.5): pad with base
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);
      var pad = Math.round(h * (1 - LID_SPEC.h / BOX.h) / 2);
      ctx.save();
      if (flip) { ctx.translate(w, 0); ctx.scale(-1, 1); }
      ctx.drawImage(wrap, sx, 0, sw, wrapH, 0, pad, w, h - 2 * pad);
      ctx.restore();
      if (notchedEdge) {
        // pale die-cut lining down the front edge, with the real notch profile
        var edgeX = notchedEdge === 'left' ? 0 : w;
        var dir = notchedEdge === 'left' ? 1 : -1;
        var sheetH = h - 2 * pad;
        var g = ctx.createLinearGradient(edgeX, 0, edgeX + dir * 18, 0);
        g.addColorStop(0, '#e6e6e8');
        g.addColorStop(1, 'rgba(230,230,232,0)');
        ctx.strokeStyle = '#d9d9dc';
        ctx.fillStyle = g;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(edgeX, pad);
        for (var n = 0; n < LID_SPEC.notches.length; n++) {
          var notch = LID_SPEC.notches[n];
          for (var i = 0; i < NOTCH_PROFILE.length; i++) {
            var p = NOTCH_PROFILE[i];
            var y = pad + (notch.top + (notch.bottom - notch.top) * p[0]) / LID_SPEC.h * sheetH;
            var x = edgeX + dir * notch.depth * p[1] / LID_SPEC.panels[0] * w;
            ctx.lineTo(x, y);
          }
        }
        ctx.lineTo(edgeX, h - pad);
        ctx.stroke();
        // slim solid lining right at the edge
        ctx.fillStyle = '#dcdcde';
        ctx.fillRect(notchedEdge === 'left' ? 0 : w - 3, pad, 3, sheetH);
      }
    }

    // Face UV facts, probed against this three build: -x face u=0 is the
    // BACK edge, +x face u=0 is the FRONT edge, -z face u=0 is the +x
    // corner. Mapping each panel so its sheet runs the right way around the
    // box therefore flips ALL THREE wrap panels horizontally, which also
    // lands each notched sheet edge (panel A x=0, panel C x=284.5) on its
    // face's FRONT edge — where the die-cut finishes on the real box.
    copyPanel(rec.texCanvas.left, xA, wSide, true, 'right');
    copyPanel(rec.texCanvas.right, xC, wSide, true, 'left');
    copyPanel(rec.texCanvas.back, xB, wBack, true, null);

    // front: the two drawer fronts (not printed)
    var f = rec.texCanvas.front.getContext('2d');
    var fw = rec.texCanvas.front.width, fh = rec.texCanvas.front.height;
    f.clearRect(0, 0, fw, fh);
    f.fillStyle = base;
    f.fillRect(0, 0, fw, fh);
    f.strokeStyle = stitch;
    f.setLineDash([6, 5]);
    f.lineWidth = 2;
    var seam = fh * 0.30;                   // upper / lower compartment split
    f.strokeRect(10, 10, fw - 20, seam - 16);
    f.strokeRect(10, seam + 8, fw - 20, fh - seam - 18);
    f.setLineDash([]);
    f.strokeStyle = this.boxColor() === 'white' ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.14)';
    f.lineWidth = 3;
    f.beginPath();
    f.moveTo(0, seam); f.lineTo(fw, seam);
    f.stroke();

    // top & bottom: plain base with a stitch border
    ['top', 'bottom'].forEach(function (face) {
      var c = rec.texCanvas[face];
      var ctx = c.getContext('2d');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = stitch;
      ctx.setLineDash([6, 5]);
      ctx.lineWidth = 2;
      ctx.strokeRect(10, 10, c.width - 20, c.height - 20);
    });

    for (var face in rec.tex) rec.tex[face].needsUpdate = true;
    rec.needsRender = true;
  };

  // ---- sleeves ----------------------------------------------------------

  BundleStudio.prototype.buildSleeveMeshes = function (rec) {
    rec.texCanvas = document.createElement('canvas');
    rec.texCanvas.width = 528;
    rec.texCanvas.height = 728;
    rec.tex = this.canvasTex(rec.renderer, rec.texCanvas);
    this.buildSleeveGeometry(rec);
    rec.near = 80; rec.far = 900;
  };

  BundleStudio.prototype.buildSleeveGeometry = function (rec) {
    var THREE = this.THREE;
    var g = rec.group;
    while (g.children.length) {
      var c = g.children.pop();
      if (c.geometry) c.geometry.dispose();
    }
    var d = this.dims('sleeves');
    var w = d.w, h = d.h;
    var shape = this.roundedShape(w, h, 1.6);

    // back film 0.12, card cavity 0.10, front film 0.09 (scaled up so the
    // sleeve reads as an object; 1/3 of the first build's exaggeration)
    var K = 2;
    var BACK_D = 0.12 * K, CARD_D = 0.10 * K, FRONT_D = 0.09 * K;
    var TOTAL = BACK_D + CARD_D + FRONT_D;
    var halfT = TOTAL / 2;

    var backGeo = new THREE.ExtrudeGeometry(shape, {
      depth: BACK_D, bevelEnabled: false, curveSegments: 16
    });
    backGeo.translate(0, 0, halfT - BACK_D);
    rec.baseMat = rec.baseMat || new THREE.MeshPhysicalMaterial({
      color: 0x101016,
      roughness: 0.55, metalness: 0.0, clearcoat: 0.3, clearcoatRoughness: 0.4
    });
    g.add(new THREE.Mesh(backGeo, rec.baseMat));

    var artGeo = this.faceGeometry(shape, w, h);
    artGeo.translate(0, 0, halfT + 0.0015);
    rec.artMat = rec.artMat || new THREE.MeshPhysicalMaterial({
      map: rec.tex,
      emissive: 0xffffff,
      emissiveMap: rec.tex,
      emissiveIntensity: 0.20,
      roughness: 0.16, metalness: 0.05,
      clearcoat: 0.9, clearcoatRoughness: 0.06,
      envMap: rec.envGloss, envMapIntensity: 0.9,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    g.add(new THREE.Mesh(artGeo, rec.artMat));

    var frontGeo = new THREE.ExtrudeGeometry(shape, {
      depth: FRONT_D, bevelEnabled: false, curveSegments: 16
    });
    frontGeo.translate(0, 0, -halfT);
    rec.frontMat = rec.frontMat || new THREE.MeshPhysicalMaterial({
      color: 0xffffff, transparent: true, opacity: 0.13,
      roughness: 0.03, metalness: 0.0,
      reflectivity: 0.9,
      clearcoat: 1.0, clearcoatRoughness: 0.01,
      depthWrite: false, side: THREE.DoubleSide
    });
    g.add(new THREE.Mesh(frontGeo, rec.frontMat));

    rec.fitW = w * 1.15; rec.fitH = h * 1.1;
  };

  /** Card Size changed: rebuild the sleeve mesh at the new dimensions. */
  BundleStudio.prototype.rebuildSleeveGeometry = function () {
    var rec = this.stages.sleeves;
    if (!rec || !this.THREE) return;
    this.buildSleeveGeometry(rec);
    if (rec.resize) rec.resize();
    rec.needsRender = true;
  };

  // ---- field center -----------------------------------------------------

  BundleStudio.prototype.buildFieldcenterMeshes = function (rec) {
    var THREE = this.THREE;
    rec.texCanvas = document.createElement('canvas');
    rec.texCanvas.width = 520;
    rec.texCanvas.height = 751;
    rec.tex = this.canvasTex(rec.renderer, rec.texCanvas);

    var w = ITEMS.fieldcenter.w, h = ITEMS.fieldcenter.h;
    var shape = this.roundedShape(w, h, ITEMS.fieldcenter.corner);

    var D = 0.6 * 1.5;      // slab, scaled so the metal edge reads (halved 2026-10-01)
    var halfT = D / 2;
    var slabGeo = new THREE.ExtrudeGeometry(shape, {
      depth: D, bevelEnabled: false, curveSegments: 16
    });
    slabGeo.translate(0, 0, -halfT);
    rec.slabMat = new THREE.MeshPhysicalMaterial({
      color: 0xc9a545,
      metalness: 0.95, roughness: 0.32,
      clearcoat: 0.6, clearcoatRoughness: 0.2
    });
    rec.group.add(new THREE.Mesh(slabGeo, rec.slabMat));

    var artGeo = this.faceGeometry(shape, w, h);
    artGeo.translate(0, 0, halfT + 0.0015);
    rec.artMat = new THREE.MeshPhysicalMaterial({
      map: rec.tex,
      emissive: 0xffffff,
      emissiveMap: rec.tex,
      emissiveIntensity: 0.32,
      metalness: 0.45, roughness: 0.22,
      clearcoat: 1.0, clearcoatRoughness: 0.06,
      envMap: rec.envGloss, envMapIntensity: 1.0,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    rec.group.add(new THREE.Mesh(artGeo, rec.artMat));

    // embossed gold back
    var backGeo = this.faceGeometry(shape, w, h);
    backGeo.translate(0, 0, halfT + 0.0015);
    rec.backMat = new THREE.MeshPhysicalMaterial({
      color: 0xd9b559,
      metalness: 0.9, roughness: 0.35,
      clearcoat: 0.6, clearcoatRoughness: 0.2,
      envMap: rec.envGloss, envMapIntensity: 1.0,
      polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2
    });
    var backMesh = new this.THREE.Mesh(backGeo, rec.backMat);
    backMesh.rotation.y = Math.PI;
    rec.group.add(backMesh);
    this.applyFcBack(rec);

    rec.fitW = w * 1.25; rec.fitH = h * 1.15;
    rec.near = 40; rec.far = 700;
  };

  BundleStudio.prototype.applyFcBack = function (rec) {
    var self = this;
    if (!this.fcBackUrl) return;
    var img = this.images['fc-back'];
    if (!img) {
      img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { self.applyFcBack(rec); };
      img.src = this.fcBackUrl;
      this.images['fc-back'] = img;
      return;
    }
    if (!img.complete || !img.naturalWidth || rec.backTex) return;
    rec.backTex = new this.THREE.Texture(img);
    rec.backTex.colorSpace = this.THREE.SRGBColorSpace;
    rec.backTex.anisotropy = rec.renderer.capabilities.getMaxAnisotropy();
    rec.backTex.needsUpdate = true;
    rec.backMat.map = rec.backTex;
    rec.backMat.color.set(0xffffff);
    rec.backMat.emissive = new this.THREE.Color(0xffffff);
    rec.backMat.emissiveMap = rec.backTex;
    rec.backMat.emissiveIntensity = 0.18;
    rec.backMat.needsUpdate = true;
    rec.needsRender = true;
  };

  // ---- shared mesh helpers ---------------------------------------------

  BundleStudio.prototype.roundedShape = function (w, h, r) {
    var THREE = this.THREE;
    var s = new THREE.Shape();
    var x = -w / 2, y = -h / 2;
    r = Math.min(r, w / 2, h / 2);
    if (!(r > 0.01)) {
      s.moveTo(x, y);
      s.lineTo(x + w, y);
      s.lineTo(x + w, y + h);
      s.lineTo(x, y + h);
      s.lineTo(x, y);
      return s;
    }
    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y);
    s.quadraticCurveTo(x + w, y, x + w, y + r);
    s.lineTo(x + w, y + h - r);
    s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    s.lineTo(x + r, y + h);
    s.quadraticCurveTo(x, y + h, x, y + h - r);
    s.lineTo(x, y + r);
    s.quadraticCurveTo(x, y, x + r, y);
    return s;
  };

  BundleStudio.prototype.faceGeometry = function (shape, w, h) {
    var THREE = this.THREE;
    var geo = new THREE.ShapeGeometry(shape, 16);
    var pos = geo.attributes.position, uv = geo.attributes.uv;
    for (var i = 0; i < pos.count; i++) {
      uv.setXY(i, (pos.getX(i) + w / 2) / w, (pos.getY(i) + h / 2) / h);
    }
    uv.needsUpdate = true;
    return geo;
  };

  /** Repaints every 3D texture + the playmat 2.5D stage from current state. */
  BundleStudio.prototype.update3D = function () {
    // playmat: CSS 2.5D — repaint its stage canvas and set the pose class
    var pane = this.pane('playmat');
    if (pane) {
      var stage = pane.querySelector('[data-bst-stage]');
      var mat = stage && stage.querySelector('[data-bst-matcanvas]');
      if (mat) {
        var d = this.dims('playmat');
        var boxW = Math.min(stage.clientWidth ? stage.clientWidth - 16 : 330, 720);
        if (boxW > 40) {
          var dpr = Math.min(window.devicePixelRatio || 1, 2);
          mat.width = Math.round(boxW * dpr);
          mat.height = Math.round(boxW * d.h / d.w * dpr);
          mat.style.width = boxW + 'px';
          mat.style.height = Math.round(boxW * d.h / d.w) + 'px';
          var ctx = mat.getContext('2d');
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          this.drawItem('playmat', ctx, boxW, Math.round(boxW * d.h / d.w), {});
        }
        stage.classList.toggle('bst-mat--table', this.matView === 'table');
        stage.classList.toggle('bst-mat--flat', this.matView !== 'table');
      }
      this.updateMatBadges();
    }

    if (!this.THREE) return;
    var recD = this.stages.deckbox;
    if (recD) this.updateDeckboxTextures(recD);
    var recS = this.stages.sleeves;
    if (recS) {
      this.drawItem('sleeves', recS.texCanvas.getContext('2d'),
        recS.texCanvas.width, recS.texCanvas.height, {});
      recS.tex.needsUpdate = true;
      recS.needsRender = true;
    }
    var recF = this.stages.fieldcenter;
    if (recF) {
      this.drawItem('fieldcenter', recF.texCanvas.getContext('2d'),
        recF.texCanvas.width, recF.texCanvas.height, {});
      recF.tex.needsUpdate = true;
      recF.needsRender = true;
    }
  };

  // -------------------------------------------------- bundle picks + gate

  BundleStudio.prototype.pickedRows = function () {
    var scope = this.wrapper || document;
    var rows = [];
    scope.querySelectorAll('[have-product-data]').forEach(function (row) {
      var idInput = row.querySelector('input[name="id[]"]');
      if (!idInput || !idInput.value) return;
      var qtyInput = row.querySelector('input[name="quantity[]"]');
      rows.push({
        productId: String(row.dataset.productId || ''),
        variantId: idInput.value,
        quantity: Math.max(1, parseInt(qtyInput ? qtyInput.value : row.dataset.productQty, 10) || 1)
      });
    });
    return rows;
  };

  /** The tab dots mark which items are in the bundle. */
  BundleStudio.prototype.syncTabDots = function () {
    var picked = {};
    var rows = this.pickedRows();
    for (var i = 0; i < rows.length; i++) {
      var t = this.typeOfProduct(rows[i].productId);
      if (t) picked[t] = true;
    }
    for (var j = 0; j < this.el.tabs.length; j++) {
      var tab = this.el.tabs[j];
      var type = tab.getAttribute('data-bst-tab');
      if (ITEMS[type]) tab.classList.toggle('bst-tab--picked', !!picked[type]);
    }
  };

  BundleStudio.prototype.watchBundle = function () {
    var self = this;
    var scope = this.wrapper || document;
    var form = scope.querySelector('[bundle-product-form]');
    this.form = form;
    this.syncTabDots();

    // option changes made on the cards re-render the studio too
    scope.addEventListener('change', function (e) {
      if (e.target.closest && (e.target.closest('[data-bundle-option-select]') ||
          e.target.closest('[data-bundle-variant-select]'))) {
        self.updateMatBadges();
        self.renderAll();
      }
    });

    if (!form) return;
    var observer = new MutationObserver(function () {
      observer.disconnect();
      try {
        self.syncTabDots();
        self.updateGate();
      } finally {
        observer.observe(form, { childList: true, subtree: true });
      }
    });
    observer.observe(form, { childList: true, subtree: true });

    this.bindSubmit(form);
    this.bindBuyGuard();
  };

  BundleStudio.prototype.updateGate = function () {
    var ready = !this.requireArtwork || !!this.art.file;
    this.root.classList.toggle('bst--ready', ready);
  };

  /** Friendly voice on top of the fail-safe CSS gate. */
  BundleStudio.prototype.bindBuyGuard = function () {
    var self = this;
    var scope = this.wrapper || document;
    var footer = scope.querySelector('.bundle-product-cart-footer');
    if (!footer || footer.__bstGuard) return;
    footer.__bstGuard = true;
    footer.addEventListener('click', function (e) {
      if (!self.requireArtwork || self.art.file) return;
      if (!e.target.closest('[add-all-bundle-product], .bundle-product-cart-actions')) return;
      e.preventDefault();
      e.stopPropagation();
      self.selectTab('upload');
      self.say('Please upload your design first — it prints on every item in the bundle.', true);
      var zone = self.el.dropzone;
      if (zone) {
        zone.classList.remove('bst-flash');
        void zone.offsetWidth;
        zone.classList.add('bst-flash');
        setTimeout(function () { zone.classList.remove('bst-flash'); }, 1200);
      }
      if (self.root.scrollIntoView) {
        self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);
  };

  // ------------------------------------------------------- capture + notes

  /** Print-scale placement snapshot for one item, as a File. */
  BundleStudio.prototype.captureShot = function (type) {
    var self = this;
    return new Promise(function (resolve) {
      try {
        var item = ITEMS[type];
        var d = self.dims(type);
        var c = document.createElement('canvas');
        if (!c.toBlob) return resolve(null);
        c.width = item.capW;
        c.height = Math.round(item.capW * d.h / d.w);
        var ctx = c.getContext('2d');
        self.drawItem(type, ctx, c.width, c.height, { guides: true, lw: Math.max(2, c.width / 800) });
        c.toBlob(function (blob) {
          resolve(blob ? new File([blob], 'placement-preview-' + type + '.jpg', { type: 'image/jpeg' }) : null);
        }, 'image/jpeg', 0.9);
      } catch (e) { resolve(null); }
    });
  };

  BundleStudio.prototype.designNote = function (type) {
    var item = ITEMS[type];
    var d = this.dims(type);
    var st = this.state[type];
    var parts = [];
    if (this.art.file && this.art.file.name) parts.push(this.art.file.name);
    parts = parts.concat([
      'Bundle ' + item.label.toLowerCase() + ' ' + d.w + 'x' + d.h + 'mm',
      'scale ' + st.scale.toFixed(2),
      'offset ' + st.tx.toFixed(1) + 'mm,' + st.ty.toFixed(1) + 'mm',
      'rotate ' + Math.round(st.rot * 180 / Math.PI) + 'deg'
    ]);
    // print-affecting choices made in the studio
    if (type === 'playmat') {
      var zv = this.zoneValue();
      if (zv) parts.push('zone ' + zv + ' ' + this.opts.zoneColor);
    }
    if (type === 'fieldcenter' && this.markingsOn()) {
      parts.push('markings ' + this.opts.markColor);
    }
    if (type === 'deckbox') parts.push('box ' + this.boxColor());
    if (this.art.img) {
      var k = coverFactor(this.art.img.naturalWidth, this.art.img.naturalHeight,
        d.w, d.h, st.rot) * st.scale;
      parts.push('src ' + Math.round(MM_PER_INCH / k * (this.art.srcScale || 1)) + 'ppi');
    }
    return parts.join(' | ');
  };

  // ----------------------------------------------------------- cart submit

  /** One /cart/add per picked item, each with its own design files. */
  BundleStudio.prototype.bindSubmit = function (form) {
    var self = this;
    if (form.__bstSubmit) return;
    form.__bstSubmit = true;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      if (self.requireArtwork && !self.art.file) {
        self.selectTab('upload');
        self.say('Please upload your design before adding the bundle to cart.', true);
        self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      var rows = self.pickedRows();
      if (!rows.length) return;
      self.submitRows(rows);
    }, true);
  };

  BundleStudio.prototype.submitRows = function (rows) {
    var self = this;
    if (this._submitting) return;
    this._submitting = true;

    var scope = this.wrapper || document;
    var btn = scope.querySelector('[add-all-bundle-product]');
    var errWrap = scope.querySelector('.product-form__error-message-wrapper');
    var errMsg = scope.querySelector('.product-form__error-message');
    var btnHtml = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding your bundle…'; }
    if (errWrap) errWrap.hidden = true;

    var bundleId = 'B' + Date.now().toString(36).toUpperCase() +
      Math.random().toString(36).slice(2, 6).toUpperCase();

    var chain = Promise.resolve();
    rows.forEach(function (row) {
      chain = chain.then(function () {
        var type = self.typeOfProduct(row.productId);
        var shotPromise = (type && self.art.file) ? self.captureShot(type) : Promise.resolve(null);
        return shotPromise.then(function (shot) {
          var fd = new FormData();
          fd.append('id', row.variantId);
          fd.append('quantity', String(row.quantity));
          fd.append('attributes[Bundle Sale]', 'Yes');
          fd.append('properties[_bundle]', bundleId);
          if (type && self.art.file) {
            fd.append('properties[Artwork]', self.art.file, self.art.file.name);
            if (shot) fd.append('properties[Design Preview]', shot, shot.name);
            fd.append('properties[_design]', self.designNote(type));
          }
          return fetch('/cart/add', { method: 'POST', body: fd }).then(function (res) {
            if (!res.ok) {
              return res.json().catch(function () { return {}; }).then(function (data) {
                throw new Error(data.description || data.message || ('Could not add an item (' + res.status + ')'));
              });
            }
          });
        });
      });
    });

    chain.then(function () {
      window.location.href = '/cart';
    }).catch(function (err) {
      console.error('[bundle-studio] add to cart failed', err);
      self._submitting = false;
      if (btn) { btn.disabled = false; btn.innerHTML = btnHtml; }
      if (errWrap && errMsg) {
        errMsg.textContent = (err && err.message) ||
          'Something went wrong adding the bundle. Nothing may have been added — please check your cart and try again.';
        errWrap.hidden = false;
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
